import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionsContext';

import LoginPage          from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage  from './pages/ResetPasswordPage';
import DashboardPage      from './pages/DashboardPage';
import ProjectsPage       from './pages/ProjectsPage';
import TasksPage          from './pages/TasksPage';
import UsersPage          from './pages/UsersPage';
import ReportsPage        from './pages/ReportsPage';
import SettingsPage       from './pages/SettingsPage';
import ProfilePage        from './pages/ProfilePage';
import DocumentsListPage  from './pages/DocumentsListPage';
import CalendarPage       from './pages/CalendarPage';
import MyTasksPage        from './pages/MyTasksPage';
import ContactsPage       from './pages/ContactsPage';
import WorkspacePage      from './pages/WorkspacePage';
import CompaniesPage      from './pages/CompaniesPage';
import SuperAdminPage     from './pages/SuperAdminPage';
import FilesPage          from './pages/FilesPage';
import ImportPage         from './pages/ImportPage';

const ProtectedRoute = ({ children }) => {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? children : <Navigate to="/login" replace />;
};

// Handle auto-login via cross-subdomain cookie set during signup
function AutoLoginHandler() {
  useEffect(() => {
    const cookies = document.cookie.split(';').reduce((acc, c) => {
      const idx = c.indexOf('=');
      if (idx < 0) return acc;
      const k = c.slice(0, idx).trim();
      const v = c.slice(idx + 1).trim();
      acc[k] = decodeURIComponent(v);
      return acc;
    }, {});

    if (!cookies.gpf_autologin) return;

    try {
      const data = JSON.parse(cookies.gpf_autologin);
      document.cookie = 'gpf_autologin=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.goprojectflow.com; secure';
      if (data.token) {
        localStorage.setItem('gpf_token', data.token);
        localStorage.setItem('gpf_user', JSON.stringify({
          name:           data.user_display_name || '',
          email:          data.user_email        || '',
          id:             String(data.user_id    || ''),
          role:           (data.user_roles       || ['gpf_administrator'])[0],
          workspace_slug: data.workspace_slug    || '',
        }));
        window.location.href = '/dashboard';
      } else if (data.user_id) {
        localStorage.setItem('gpf_pending_user', JSON.stringify({
          name:     data.user_display_name || '',
          email:    data.user_email        || '',
          username: data.user_nicename     || '',
        }));
        window.location.href = '/login?welcome=1';
      }
    } catch(e) { console.error('Auto-login error:', e); }
  }, []);
  return null;
}

function AppRoutes() {
  const { isLoggedIn } = useAuth();
  return (
    <>
      <AutoLoginHandler />
      <Routes>
      <Route path="/login"           element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password"  element={<ResetPasswordPage />} />
      <Route path="/dashboard"       element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/projects"        element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/projects/:projectId/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/tasks"           element={<ProtectedRoute><MyTasksPage /></ProtectedRoute>} />
      <Route path="/users"           element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/reports"         element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/settings"        element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/profile"         element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/documents"       element={<ProtectedRoute><DocumentsListPage /></ProtectedRoute>} />
      <Route path="/calendar"        element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/contacts"        element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
      <Route path="/companies"       element={<ProtectedRoute><CompaniesPage /></ProtectedRoute>} />
      <Route path="/workspace"       element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
      <Route path="/platform-admin"  element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />
      <Route path="/files"           element={<ProtectedRoute><FilesPage /></ProtectedRoute>} />
      <Route path="/import"          element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
      <Route path="*"                element={<Navigate to={isLoggedIn ? "/dashboard" : "/login"} replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PermissionsProvider>
          <AppRoutes />
        </PermissionsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;